import { formatFairPayDate } from "@components/utils/fairPayFormat";

/** Backend PayRange.get_table_name() — used by generic chatter list API. */
export const PAY_RANGE_CHATTER_TABLE = "payrange";

/** One thread per published pay range version (ER-12068 / ER-12599). */
export const PAY_RANGE_VERSION_CHATTER_TABLE = "pay_range_version";

const PAY_RANGE_CHATTER_LABELS = {
  "pay_range.create_draft": "Created a draft pay range",
  "pay_range.edit_draft": "Edited the draft pay range",
  "pay_range.publish": "Published this pay range",
  "pay_range.version_draft_saved": "Saved a new version draft",
  "pay_range.version_draft_discarded": "Discarded the version draft",
  "pay_range.publish_version_draft": "Published a version draft",
  "pay_range.rename": "Renamed this pay range",
  "pay_range.retire": "Retired this pay range",
  "pay_range.reactivate": "Reactivated this pay range",
  "pay_range.archive": "Archived this pay range",
  "pay_range.unarchive": "Unarchived this pay range",
  "pay_range.unarchive_and_rename": "Unarchived and renamed this pay range",
  "pay_range.correct_version": "Corrected a version",
};

const CORRECT_VERSION_FIELDS = [
  { label: "Effective from", key: "effective_from", kind: "date" },
  { label: "Effective to", key: "effective_to", kind: "date" },
  { label: "Min", key: "min_amount", kind: "amount" },
  { label: "Max", key: "max_amount", kind: "amount" },
];

function formatCorrectedValue(value, kind) {
  if (value == null || value === "") {
    return kind === "date" ? "open-ended" : "—";
  }
  if (kind === "date") return formatFairPayDate(value) || String(value);
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function sameCorrectedValue(a, b) {
  return (a ?? null) === (b ?? null);
}

function isCorrectionPayload(value) {
  if (!value || typeof value !== "object") return false;
  if (value.action === "pay_range.correct_version") return true;
  return CORRECT_VERSION_FIELDS.some(({ key }) => `old_${key}` in value);
}

/**
 * The correction payload travels as `payload` on the range thread, but the
 * version thread persists only the serialised payload in `note` (ER-12599).
 */
function readCorrectionPayload(entry = {}) {
  if (isCorrectionPayload(entry.payload)) return entry.payload;
  if (typeof entry.note === "string" && entry.note.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(entry.note);
      if (isCorrectionPayload(parsed)) return parsed;
    } catch {
      // Not JSON: fall through to the note/label handling below.
    }
  }
  return null;
}

/** "Corrected Version 2" plus one "Field: before → after" line per change (ER-12600). */
function formatCorrectVersionMessage(payload) {
  const heading =
    payload.version_number != null
      ? `Corrected Version ${payload.version_number}`
      : "Corrected a version";
  const lines = CORRECT_VERSION_FIELDS.filter(
    ({ key }) =>
      `old_${key}` in payload &&
      !sameCorrectedValue(payload[`old_${key}`], payload[key])
  ).map(
    ({ label, key, kind }) =>
      `${label}: ${formatCorrectedValue(payload[`old_${key}`], kind)} → ${formatCorrectedValue(
        payload[key],
        kind
      )}`
  );
  return [heading, ...lines].join("\n");
}

export function formatPayRangeChatterMessage(entry = {}) {
  const code = entry.action_code;

  // Special case: pay_range.retire formats from payload.retired_on (ER-11220)
  if (code === "pay_range.retire" && entry.payload?.retired_on) {
    const formatted = formatFairPayDate(entry.payload.retired_on);
    if (formatted) return `Retired this pay range effective ${formatted}`;
    // Fall through to note/label if date parsing fails
  }

  if (code === "pay_range.correct_version") {
    const payload = readCorrectionPayload(entry);
    if (payload) return formatCorrectVersionMessage(payload);
  }

  if (entry.note) return entry.note;
  if (code && PAY_RANGE_CHATTER_LABELS[code]) {
    return PAY_RANGE_CHATTER_LABELS[code];
  }
  if (code) {
    return String(code)
      .replace(/^pay_range\./, "")
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());
  }
  return "";
}

export function transformPayRangeChatterResults(results = []) {
  return results.map((el) => {
    const date = new Date(el.recorded_on);
    const nameParts = el.recorded_by_name
      ? String(el.recorded_by_name).split(" ")
      : [""];

    return {
      id: el.id,
      avatar:
        nameParts.length >= 2
          ? {
              picture: "",
              firstName: nameParts[0],
              lastName: nameParts.slice(1).join(" "),
            }
          : {
              picture: "",
              firstName: el.recorded_by_name || "",
              lastName: "",
            },
      IsExIQtiveBot: false,
      message: formatPayRangeChatterMessage(el),
      created_on: date.getTime().toString(),
      IsHistory: el.is_history,
    };
  });
}
